import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryDetails } from '../../../../hooks/QueryBuilder';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Divider, IconButton,
    Chip, CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, ContentCopy as CopyIcon, Add as AddIcon } from '@mui/icons-material';
import { QueryGroup, QueryField, QueryRule, QuerySource } from '../../../../types/QueryBuilder';
import QueryRuleGroup from './QueryRuleGroup';
import SimpleQueryGroup from './SimpleQueryGroup';
import GroupSelector from './GroupSelector';
import { convertSavedQueryToGroups, getSimpleQueryFields, toQuerySource } from '../../../../utils/queryBuilderUtils';
import { toast } from 'react-toastify';

interface EditQueryDialogProps {
    open: boolean;
    onClose: () => void;
    query: Record<string, unknown> | null;
    fields: QueryField[];
    categories: string[];
    availableGroups: Array<{ id: number; description: string }>;
    loadingGroups: boolean;
    onRefetchGroups: () => void;
    onSave: (updatedQuery: Record<string, unknown>, mode: 'modify' | 'copy') => void;
}

const DEFAULT_GROUP: QueryGroup = { id: 'group-1', combinator: 'AND', rules: [] };

function buildDefaultGroup(fields: QueryField[], categories: string[], source: QuerySource): QueryGroup {
    if (source === 'SIMPLE') {
        return { id: 'group-1', combinator: 'AND', rules: [] };
    }
    const fieldsInCat = fields.filter(f => f.Categorie === categories[0]);
    return {
        id: 'group-1',
        combinator: 'AND',
        rules: [{
            id: Date.now().toString(),
            category: categories[0],
            field: fieldsInCat[0]?.name,
            operator: '=',
            value: '',
        }],
    };
}

function mapRulesToGroups(convertedGroups: QueryGroup[], fields: QueryField[], categories: string[]): QueryGroup[] {
    return convertedGroups.map(group => ({
        ...group,
        combinator: 'AND' as const,
        rules: group.rules.map((rule) => {
            const field = fields.find(f => f.name === rule.field);
            return {
                ...rule,
                category: field?.Categorie || categories[0],
                field: field ? rule.field : fields[0]?.name,
                operator: rule.operator || '=',
                value: rule.value || '',
            };
        }),
    }));
}

export function EditQueryDialog({
    open, onClose, query, fields, categories,
    availableGroups, loadingGroups, onRefetchGroups, onSave,
}: EditQueryDialogProps) {
    const [queryDescription, setQueryDescription] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [groups, setGroups] = useState<QueryGroup[]>([DEFAULT_GROUP]);
    const [source, setSource] = useState<QuerySource>('SIMPLE');
    const [originalQuery, setOriginalQuery] = useState<Record<string, unknown> | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const { fetchQueryDetails } = useQueryDetails();
    const fieldsRef = useRef(fields);
    const categoriesRef = useRef(categories);
    fieldsRef.current = fields;
    categoriesRef.current = categories;
    const simpleFields = getSimpleQueryFields(fields);

    useEffect(() => {
        if (!open || !query || !fields.length || !categories.length) return;

        const queryId = String(query.QUERY_ID ?? query.query_id ?? '');
        if (!queryId) return;

        const loadFromApi = async () => {
            setLoadingDetails(true);
            try {
                const details = await fetchQueryDetails(queryId);
                if (!details) {
                    const fallbackSource = toQuerySource(query.SOURCE);
                    setSource(fallbackSource);
                    setGroups([buildDefaultGroup(fieldsRef.current, categoriesRef.current, fallbackSource)]);
                    return;
                }

                // Remplir les champs depuis la réponse API (source de vérité)
                setOriginalQuery({ ...query, ...details });
                setQueryDescription(String(details.QUERY_NAME || details.DESCRIPTION || query.QUERY_NAME || query.DESCRIPTION || ''));
                const groupId = details.GROUP_QUERY_ID ?? null;
                setSelectedGroupId(groupId && groupId !== 'null' ? Number(groupId) : null);
                const querySource = toQuerySource(details.SOURCE ?? query.SOURCE);
                setSource(querySource);

                let conditions = details.conditions ?? null;
                if (typeof conditions === 'string') {
                    try { conditions = JSON.parse(conditions); } catch { conditions = null; }
                }

                if (conditions) {
                    try {
                        const converted = convertSavedQueryToGroups({ ...details, conditions } as Parameters<typeof convertSavedQueryToGroups>[0]);
                        if (converted?.length && converted[0].rules.length) {
                            setGroups(mapRulesToGroups(converted, fieldsRef.current, categoriesRef.current));
                            return;
                        }
                    } catch {}
                }
                setGroups([buildDefaultGroup(fieldsRef.current, categoriesRef.current, querySource)]);
            } finally {
                setLoadingDetails(false);
            }
        };

        loadFromApi();
    }, [open, query, fields, categories]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        setQueryDescription('');
        setSelectedGroupId(null);
        setGroups([DEFAULT_GROUP]);
        setSource('SIMPLE');
        setOriginalQuery(null);
        onClose();
    };

    const addRule = (groupId: string) => {
        const defaultCategory = categories[0] || '';
        const defaultField = fields.filter(f => f.Categorie === defaultCategory)[0]?.name || '';
        setGroups(groups.map(group =>
            group.id === groupId
                ? { ...group, rules: [...group.rules, { id: Date.now().toString(), category: defaultCategory, field: defaultField, operator: '=' as const, value: '' }] }
                : group
        ));
    };

    const removeRule = (groupId: string, ruleId: string) => {
        setGroups(groups.map(group =>
            group.id === groupId ? { ...group, rules: group.rules.filter(r => r.id !== ruleId) } : group
        ));
    };

    const updateRule = (groupId: string, ruleId: string, data: object) => {
        setGroups(groups.map(group =>
            group.id === groupId
                ? { ...group, rules: group.rules.map(r => r.id === ruleId ? { ...r, ...data } : r) }
                : group
        ));
    };

    const updateSimpleField = useCallback((fieldName: string, data: Partial<QueryRule>) => {
        setGroups(prevGroups => prevGroups.map((group, index) => {
            if (index !== 0) return group;
            const hasRule = group.rules.some(r => r.field === fieldName);
            return {
                ...group,
                rules: hasRule
                    ? group.rules.map(r => r.field === fieldName ? { ...r, ...data } : r)
                    : [...group.rules, { id: Date.now().toString(), field: fieldName, operator: '=' as const, ...data }],
            };
        }));
    }, []);

    const addGroup = () => {
        if (groups.length < 2) {
            setGroups([...groups, { id: `group-${Date.now()}`, combinator: 'AND', rules: [] }]);
        }
    };

    const removeGroup = (groupId: string) => {
        setGroups(groups.filter(g => g.id !== groupId));
    };

    const handleModify = () => {
        if (!queryDescription.trim()) return toast.warning('Veuillez renseigner un nom de requête');
        onSave({ ...originalQuery, QUERY_NAME: queryDescription, DESCRIPTION: queryDescription, groups, groupId: selectedGroupId, source }, 'modify');
        handleClose();
    };

    const handleSaveCopy = () => {
        if (!queryDescription.trim()) return toast.warning('Veuillez renseigner un nom de requête');
        const desc = `Copie - ${queryDescription}`;
        onSave({ ...originalQuery, QUERY_ID: undefined, QUERY_NAME: desc, DESCRIPTION: desc, groups, groupId: selectedGroupId, source }, 'copy');
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Modifier la requête</Typography>
                    <IconButton onClick={handleClose}><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loadingDetails ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : <>
                    <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Description de la requête *</Typography>
                            <TextField
                                fullWidth
                                value={queryDescription}
                                onChange={(e) => setQueryDescription(e.target.value)}
                                placeholder="Ex: Clients fidèles - Carte FIDELITE"
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Groupe de requête</Typography>
                            <GroupSelector
                                selectedGroupId={selectedGroupId}
                                availableGroups={availableGroups}
                                loading={loadingGroups}
                                fullWidth
                                onGroupIdChange={(id) => setSelectedGroupId(id)}
                                onCreateGroup={() => {}}
                                onRefetchGroups={onRefetchGroups}
                            />
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Critères de la requête</Typography>

                        {source === 'SIMPLE' ? (
                            <SimpleQueryGroup
                                fields={simpleFields}
                                rules={groups[0]?.rules ?? []}
                                onUpdateField={updateSimpleField}
                            />
                        ) : (
                            <>
                                {groups.map((group, index) => (
                                    <Box key={group.id}>
                                        {index > 0 && (
                                            <Box sx={{ textAlign: 'center', my: 2 }}>
                                                <Typography sx={{ display: 'inline-block', bgcolor: '#ff9800', color: 'white', px: 2, py: 0.5, borderRadius: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                                                    OU
                                                </Typography>
                                            </Box>
                                        )}
                                        <QueryRuleGroup
                                            group={group}
                                            categories={categories}
                                            fields={fields}
                                            onAddRule={() => addRule(group.id)}
                                            onUpdateRule={(ruleId, data) => updateRule(group.id, ruleId, data)}
                                            onRemoveRule={(ruleId) => removeRule(group.id, ruleId)}
                                            onRemoveGroup={() => removeGroup(group.id)}
                                            canRemoveGroup={groups.length > 1}
                                        />
                                    </Box>
                                ))}

                                {groups.length < 2 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={addGroup} sx={{ textTransform: 'none' }}>
                                            Ajouter un groupe (OU)
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                </> }
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button variant="outlined" startIcon={<CopyIcon />} onClick={handleSaveCopy} sx={{ textTransform: 'none' }}>
                    Enregistrer une copie
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={handleClose} sx={{ textTransform: 'none' }}>Annuler</Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleModify} sx={{ textTransform: 'none', bgcolor: '#556ee6' }}>
                        Modifier
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default EditQueryDialog;
